import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    Box,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    FormHelperText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ModalWrapper from '../../../../components/common/Modal/ModalWrapper';
import { postRequest, showMessage } from '../../../../config';
import { useDispatch } from 'react-redux';
import { toggleModal } from '../../../../reducers/modalReducer';
import { trackPopupOpen, trackPopupClose, trackFormSubmit, trackButtonClick } from '../../../../utils/gtm';

const schema = yup.object().shape({
    code: yup.string()
        .required('Please enter a coupon code')
        .matches(/^[A-Z0-9]+$/, 'Coupon code can only contain capital letters and numbers')
        .min(3, 'Coupon code must be at least 3 characters')
        .max(20, 'Coupon code cannot exceed 20 characters'),
    discountType: yup.string()
        .required('Please select a discount type'),
    discountValue: yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required('Please enter a discount value')
        .min(0, 'Discount value cannot be negative')
        .when('discountType', {
            is: 'percentage',
            then: (schema) => schema.max(100, 'Percentage cannot be more than 100%')
        }),
    duration: yup.string()
        .required('Please select a duration'),
    validFrom: yup.date()
        .required('Please select a valid from date')
        .nullable(),
    validUntil: yup.date()
        .required('Please select a valid until date')
        .nullable()
        .min(yup.ref('validFrom'), 'Valid until date must be after valid from date'),
    isActive: yup.boolean()
        .required('Please select a status')
});

const CreateDiscountCoupon = ({ fetchCoupons, selectedCoupon }) => {
    const dispatch = useDispatch();
    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            code: '',
            discountType: 'percentage',
            discountValue: '',
            duration: 'once',
            validFrom: null,
            validUntil: null,
            isActive: true
        }
    });

    useEffect(() => {

        // Track popup open
        trackPopupOpen('create_discount_coupon');

        if (selectedCoupon) {
            // Set all form values individually
            setValue('code', selectedCoupon.code);
            setValue('discountType', selectedCoupon.discountType);
            setValue('discountValue', selectedCoupon.discountValue);
            setValue('duration', selectedCoupon.duration);
            if (selectedCoupon.durationInMonths !== undefined) {
                setValue('durationInMonths', selectedCoupon.durationInMonths);
            }
            setValue('isActive', selectedCoupon.isActive);
            setValue('validFrom', new Date(selectedCoupon.validFrom));
            setValue('validUntil', new Date(selectedCoupon.expiresAt));
        } else {
            reset({
                code: '',
                discountType: 'percentage',
                discountValue: '',
                duration: 'forever',
                validFrom: null,
                validUntil: null,
                isActive: true
            }); // Reset form to default values if no coupon is selected
        }

        return () => {
            // Track popup close
            trackPopupClose('create_discount_coupon');
        };
    }, [selectedCoupon, setValue, reset]);


    const onSubmit = async (data) => {
        try {
            const formData = {
                ...data,
                expiresAt: data.validUntil,
                validFrom: data.validFrom
            };
            let endpoint = '/payment/createCoupon';
            if (selectedCoupon) {
                endpoint = `/payment/updateCoupon/${selectedCoupon._id}`
            }

            // Track form submission
            trackFormSubmit('discount_coupon_form', {
                action: selectedCoupon ? 'update' : 'create',
                coupon_code: data.code,
                discount_type: data.discountType,
                discount_value: data.discountValue
            });

            const response = await postRequest(endpoint, formData);
            dispatch(toggleModal({ open: "" }));
            showMessage(response.message)
        } catch (error) {
            console.log(error);
            showMessage(error.message)
        } finally {
            fetchCoupons();
            reset({
                code: '',
                discountType: 'percentage',
                discountValue: '',
                duration: 'forever',
                validFrom: null,
                validUntil: null,
                isActive: true
            })
        }
    };

    return (
        <ModalWrapper
            id='createCoupon'
            modalType='createCoupon'
            headerType='primary'
            heading='Create Discount Coupon'
        >
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Coupon Code"
                            {...register('code')}
                            error={!!errors.code}
                            helperText={errors.code?.message}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={!!errors.discountType}>
                            <InputLabel>Discount Type</InputLabel>
                            <Select
                                value={watch('discountType')}
                                onChange={(e) => setValue('discountType', e.target.value)}
                                label="Discount Type"
                            >
                                <MenuItem value="percentage">Percentage</MenuItem>
                                <MenuItem value="fixed">Fixed Amount</MenuItem>
                            </Select>
                            {errors.discountType && (
                                <FormHelperText>{errors.discountType.message}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={`Discount Value ${watch('discountType') === 'percentage' ? '(%)' : '(£)'}`}
                            type="number"
                            {...register('discountValue')}
                            error={!!errors.discountValue}
                            helperText={errors.discountValue?.message}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.duration}>
                            <InputLabel>Duration</InputLabel>
                            <Select
                                value={watch('duration')}
                                onChange={(e) => setValue('duration', e.target.value)}
                                label="Duration"
                            >
                                <MenuItem value="once">One-time use</MenuItem>
                                <MenuItem value="forever">Unlimited use</MenuItem>
                                <MenuItem value="repeating">Repeating</MenuItem>
                            </Select>
                            {errors.duration && (
                                <FormHelperText>{errors.duration.message}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {watch('duration') === 'repeating' && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Duration in Months"
                                type="number"
                                {...register('durationInMonths')}
                                error={!!errors.durationInMonths}
                                helperText={errors.durationInMonths?.message}
                                inputProps={{ min: 1, max: 12 }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Valid From"
                                value={watch('validFrom')}
                                onChange={(date) => setValue('validFrom', date)}
                                className='w100'
                                format="dd/MM/yyyy"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        error={!!errors.validFrom}
                                        helperText={errors.validFrom?.message}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Valid Until"
                                value={watch('validUntil')}
                                onChange={(date) => setValue('validUntil', date)}
                                className='w100'
                                format="dd/MM/yyyy"
                                minDate={watch('validFrom')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        error={!!errors.validUntil}
                                        helperText={errors.validUntil?.message}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.isActive}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={watch('isActive')}
                                onChange={(e) => setValue('isActive', e.target.value)}
                                label="Status"
                            >
                                <MenuItem value={true}>Active</MenuItem>
                                <MenuItem value={false}>Inactive</MenuItem>
                            </Select>
                            {errors.isActive && (
                                <FormHelperText>{errors.isActive.message}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    trackButtonClick('cancel_button');
                                    dispatch(toggleModal({ open: "" }));
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                            >
                                {selectedCoupon ? "Update Coupon" : "Create Coupon"}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </ModalWrapper>
    );
};

export default CreateDiscountCoupon; 